<div class="card border-0 shadow-sm hours-settings-card">
  <!-- Header -->
  <div class="card-header bg-white">
    <h5 class="mb-1 fw-semibold">
      <i class="bi bi-clock-fill text-primary me-2"></i>
      Operating Hours
    </h5>

    <small class="text-muted">
      Set the practice schedule patients see when booking appointments and note any planned
      closures.
    </small>
  </div>

  <div class="card-body">
    @if (isLoading) {
      <div class="text-center py-4 text-muted">
        <div class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></div>
        Loading operating hours...
      </div>
    }

    @if (!isLoading) {
      <div class="alert alert-info d-flex align-items-start hours-note">
        <i class="bi bi-info-circle-fill me-2 mt-1"></i>
        <div>
          Keep these hours current so patients can only book when the practice is available to see
          them.
        </div>
      </div>

      <div class="row g-3 mb-4">
        <div class="col-md-4">
          <div class="card hours-metric h-100 border-0">
            <div class="card-body">
              <div class="text-muted small mb-1">Weekday Schedule</div>
              <div class="fs-4 fw-bold">
                {{ operatingHours.weekdayOpen }} - {{ operatingHours.weekdayClose }}
              </div>
            </div>
          </div>
        </div>

        <div class="col-md-4">
          <div class="card hours-metric h-100 border-0">
            <div class="card-body">
              <div class="text-muted small mb-1">Weekend Access</div>
              <div class="fs-4 fw-bold">
                {{
                  operatingHours.saturdayOpen || operatingHours.sundayOpen ? 'Available' : 'Closed'
                }}
              </div>
            </div>
          </div>
        </div>

        <div class="col-md-4">
          <div class="card hours-metric h-100 border-0">
            <div class="card-body">
              <div class="text-muted small mb-1">Planned Closures</div>
              <div class="fs-4 fw-bold">{{ operatingHours.holidays.length }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="card border-0 bg-light hours-form-card">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-start mb-4 flex-wrap gap-2">
            <div>
              <h6 class="mb-1 fw-semibold">Practice Schedule</h6>
              <small class="text-muted"
                >Configure standard hours, weekend availability, and closures.</small
              >
            </div>

            <span class="badge text-bg-primary align-self-center"> Booking availability </span>
          </div>

          <!-- Weekday Hours -->
          <div class="mb-4">
            <h6 class="fw-semibold mb-3">Monday - Friday</h6>

            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label">Opening Time</label>
                <input type="time" class="form-control" [(ngModel)]="operatingHours.weekdayOpen" />
              </div>

              <div class="col-md-6">
                <label class="form-label">Closing Time</label>
                <input type="time" class="form-control" [(ngModel)]="operatingHours.weekdayClose" />
              </div>
            </div>
          </div>

          <hr class="hours-divider" />

          <!-- Saturday -->
          <div class="mb-4">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h6 class="fw-semibold mb-1">Saturday</h6>

                <small class="text-muted"> Configure Saturday operating hours. </small>
              </div>

              <div class="form-check form-switch">
                <input
                  class="form-check-input"
                  type="checkbox"
                  [(ngModel)]="operatingHours.saturdayOpen"
                />
              </div>
            </div>

            @if (operatingHours.saturdayOpen) {
              <div class="row g-3">
                <div class="col-md-6">
                  <label class="form-label">Opening Time</label>
                  <input
                    type="time"
                    class="form-control"
                    [(ngModel)]="operatingHours.saturdayStart"
                  />
                </div>

                <div class="col-md-6">
                  <label class="form-label">Closing Time</label>
                  <input
                    type="time"
                    class="form-control"
                    [(ngModel)]="operatingHours.saturdayEnd"
                  />
                </div>
              </div>
            }
          </div>

          <hr class="hours-divider" />

          <!-- Sunday -->
          <div>
            <div class="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h6 class="fw-semibold mb-1">Sunday</h6>

                <small class="text-muted"> Configure Sunday operating hours. </small>
              </div>

              <div class="form-check form-switch">
                <input
                  class="form-check-input"
                  type="checkbox"
                  [(ngModel)]="operatingHours.sundayOpen"
                />
              </div>
            </div>

            @if (operatingHours.sundayOpen) {
              <div class="row g-3">
                <div class="col-md-6">
                  <label class="form-label">Opening Time</label>
                  <input
                    type="time"
                    class="form-control"
                    [(ngModel)]="operatingHours.sundayStart"
                  />
                </div>

                <div class="col-md-6">
                  <label class="form-label">Closing Time</label>
                  <input type="time" class="form-control" [(ngModel)]="operatingHours.sundayEnd" />
                </div>
              </div>
            }
          </div>

          <hr class="hours-divider" />

          <!-- Holidays -->
          <div>
            <div class="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h6 class="fw-semibold mb-1">Holidays & Closures</h6>

                <small class="text-muted"> Appointments cannot be booked on these dates. </small>
              </div>

              <button class="btn btn-outline-primary btn-sm" (click)="addHoliday()">
                <i class="bi bi-plus-circle me-1"></i>
                Add Holiday
              </button>
            </div>

            @if (operatingHours.holidays.length === 0) {
              <div class="text-center py-4 text-muted border rounded bg-light">
                No holidays configured.
              </div>
            }

            @for (
              holiday of operatingHours.holidays;
              track trackHoliday($index, holiday);
              let i = $index
            ) {
              <div class="card mb-2 border">
                <div class="card-body py-3">
                  <div class="row align-items-end">
                    <div class="col-md-5">
                      <label class="form-label"> Holiday Name </label>

                      <input
                        type="text"
                        class="form-control"
                        placeholder="Christmas Day"
                        [(ngModel)]="holiday.name"
                      />
                    </div>

                    <div class="col-md-4">
                      <label class="form-label"> Date </label>

                      <input type="date" class="form-control" [(ngModel)]="holiday.date" />
                    </div>

                    <div class="col-md-3 text-end">
                      <button class="btn btn-outline-danger" (click)="removeHoliday(i)">
                        <i class="bi bi-trash"></i>
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            }
          </div>
        </div>
      </div>
    }
  </div>

  @if (!isLoading) {
    <div
      class="card-footer bg-white d-flex justify-content-between align-items-center flex-wrap gap-2"
    >
      <small class="text-muted"> Saved hours apply to future patient booking availability. </small>

      <button
        class="btn btn-primary"
        [disabled]="isSaving || isLoading"
        (click)="saveOperatingHours()"
      >
        <i class="bi bi-floppy-fill me-2"></i>
        {{ isSaving ? 'Saving...' : 'Save Operating Hours' }}
      </button>
    </div>
  }
</div>
